(() => {
  if (customElements.get('zoo-collection-description')) return;

  class ZooCollectionDescription extends HTMLElement {
    constructor() {
      super();
      this.expanded = false;
      this.focusableState = new Map();
      this.onToggle = this.toggle.bind(this);
      this.onResize = this.scheduleMeasure.bind(this);
    }

    connectedCallback() {
      this.content = this.querySelector('.collection-bottom-description__inner');
      this.button = this.querySelector('.collection-bottom-description__button');
      if (!this.content || !this.button || this.dataset.collapsible !== 'true') return;

      this.active = true;
      this.button.addEventListener('click', this.onToggle);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.content.addEventListener('load', this.onResize, true);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.content);
      }
      if (document.fonts) document.fonts.ready.then(() => this.scheduleMeasure());
      this.measure();
    }

    disconnectedCallback() {
      this.active = false;
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.content?.removeEventListener('load', this.onResize, true);
      this.button?.removeEventListener('click', this.onToggle);
      this.restoreFocusability();
      this.classList.remove('is-collapsed');
      if (this.button) this.button.hidden = true;
    }

    scheduleMeasure() {
      if (!this.active || this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    }

    measure() {
      if (!this.active || !this.content.clientWidth) return;
      const styles = getComputedStyle(this.content);
      const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.65;
      const lines = Math.min(15, Math.max(5, parseInt(this.dataset.previewLines, 10) || 10));
      const padding = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      const borders = (parseFloat(styles.borderTopWidth) || 0) + (parseFloat(styles.borderBottomWidth) || 0);
      const previewHeight = lineHeight * lines + padding;
      this.previewHeight = previewHeight + borders;
      this.hasOverflow = this.content.scrollHeight > previewHeight + 2;
      this.content.style.setProperty('--description-preview-height', `${this.previewHeight}px`);
      this.update();
    }

    toggle() {
      if (!this.hasOverflow) return;
      this.expanded = !this.expanded;
      this.update();
      if (!this.expanded && this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }

    update() {
      this.restoreFocusability();
      const fadeHeight = (parseFloat(getComputedStyle(this.content).fontSize) || 14) * 2.4;
      const cutoff = this.content.getBoundingClientRect().top + this.previewHeight - fadeHeight;
      const activeElement = this.ownerDocument.activeElement;
      if (this.hasOverflow && !this.expanded && activeElement && this.content.contains(activeElement) && activeElement.getBoundingClientRect().bottom > cutoff) {
        this.expanded = true;
      }
      const collapsed = this.hasOverflow && !this.expanded;
      this.classList.toggle('is-collapsed', collapsed);
      this.button.hidden = !this.hasOverflow;
      this.button.setAttribute('aria-expanded', String(!collapsed));
      this.button.textContent = collapsed ? this.dataset.moreLabel : this.dataset.lessLabel;
      if (!collapsed) return;

      this.content.querySelectorAll('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"], audio[controls], video[controls], summary').forEach((element) => {
        if (element.getBoundingClientRect().bottom > cutoff) {
          this.focusableState.set(element, element.getAttribute('tabindex'));
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    restoreFocusability() {
      this.focusableState.forEach((tabIndex, element) => {
        if (tabIndex === null) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', tabIndex);
      });
      this.focusableState.clear();
    }
  }

  customElements.define('zoo-collection-description', ZooCollectionDescription);
})();
